import React, { useCallback, useEffect, useRef, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import {
  mdiCalendarMonthOutline,
  mdiChevronLeft,
  mdiChevronRight,
  mdiCurrencyInr,
  mdiLightningBolt,
  mdiPencilOutline,
  mdiPowerPlugOutline,
} from "@mdi/js";
import Main from "../../components/layout/Main";
import MdiIcon from "../../components/device/app/MdiIcon";
import { useToggleTop } from "../../components/common/ToggleTop";
import { UserService } from "../../services/UserService";
import "../../assets/styles/app-energy.css";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0";
  return number % 1 === 0 ? String(number) : number.toFixed(2);
};

const formatCurrency = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "₹0.00";
  const amount = Math.abs(number).toFixed(2);
  return number < 0 ? `− ₹${amount}` : `₹${amount}`;
};

// A month's energy bill for an energy meter, ported from the app's
// app/screens/EnergyMeterExpense.js. Applying a cost per unit saves it with
// the meter; the API sends it back with each month after that.
function EnergyMeterExpense() {
  const location = useLocation();
  const toast = useToggleTop();
  const device = location.state?.data;
  const deviceId = device?.deviceId || device?.applianceId;
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [unitCost, setUnitCost] = useState("");
  const [editingCost, setEditingCost] = useState(true);
  const [costError, setCostError] = useState("");
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(false);
  // Only the latest request may update the page.
  const latestRequest = useRef(0);

  const load = useCallback(
    async (rate = null) => {
      if (!deviceId) return;
      const request = ++latestRequest.current;
      setLoading(true);
      try {
        const response = await UserService.getEnergyMeterExpense(deviceId, year, month, rate);
        if (request !== latestRequest.current) return;
        setExpense(response ?? null);
        const saved = response?.per_unit_cost;
        if (saved !== undefined && saved !== null && saved !== "") {
          setUnitCost(String(saved));
          setEditingCost(false);
          setCostError("");
        } else {
          setUnitCost("");
          setEditingCost(true);
        }
      } catch (error) {
        if (request !== latestRequest.current) return;
        setExpense(null);
        toast("Couldn't load the expense. Please try again.", "red");
      } finally {
        if (request === latestRequest.current) setLoading(false);
      }
    },
    // toast is a new function on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deviceId, year, month]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Ignore replies that arrive after leaving the page.
  useEffect(
    () => () => {
      latestRequest.current += 1;
    },
    []
  );

  if (!device) return <Redirect to="/dashboard" />;

  const handleCostChange = (event) => {
    const cleaned = event.target.value.replace(/[^0-9.]/g, "");
    if ((cleaned.match(/\./g) || []).length > 1) return;
    setUnitCost(cleaned);
    setCostError("");
  };

  const applyCost = () => {
    const value = unitCost.trim();
    if (!value) {
      setCostError("Please enter unit cost");
      return;
    }
    const cost = Number(value);
    if (!Number.isFinite(cost) || cost <= 0) {
      setCostError("Please enter a valid unit cost");
      return;
    }
    setCostError("");
    setEditingCost(false);
    load(cost);
  };

  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1;

  const changeMonth = (step) => {
    if (step > 0 && isCurrentMonth) return;
    const next = new Date(year, month - 1 + step, 1);
    setMonth(next.getMonth() + 1);
    setYear(next.getFullYear());
  };

  const consumed = Number(expense?.total_consumed_energy_kwh) || 0;
  const generated = Number(expense?.total_generated_energy_kwh) || 0;
  const netUsage = Number(expense?.energy_difference_kwh) || 0;
  const totalPrice = Number(expense?.total_price) || 0;

  return (
    <Main header="stack" title="Expenses" className="is-white-page">
      <div className="hx-emx">
        {loading ? (
          <div className="hx-emx-loading">
            <span className="hx-em-spinner is-large" aria-label="Loading" />
          </div>
        ) : (
          <>
            <div className="hx-emx-month">
              <button type="button" className="hx-emx-month-arrow" aria-label="Previous month" onClick={() => changeMonth(-1)}>
                <MdiIcon path={mdiChevronLeft} size={27} />
              </button>
              <span className="hx-emx-month-text">
                {MONTHS[(expense?.month || month) - 1]} {expense?.year || year}
              </span>
              <button
                type="button"
                className="hx-emx-month-arrow"
                aria-label="Next month"
                disabled={isCurrentMonth}
                onClick={() => changeMonth(1)}
              >
                <MdiIcon path={mdiChevronRight} size={27} />
              </button>
            </div>

            <div className="hx-emx-energy">
              <div className="hx-emx-card">
                <span className="hx-emx-card-icon" aria-hidden="true">
                  <MdiIcon path={mdiLightningBolt} size={28} />
                </span>
                <div className="hx-emx-card-label">Consumed</div>
                <div className="hx-emx-card-value">
                  {formatNumber(consumed)}
                  <small> kWh</small>
                </div>
              </div>
              <div className="hx-emx-card is-generated">
                <span className="hx-emx-card-icon" aria-hidden="true">
                  <MdiIcon path={mdiPowerPlugOutline} size={28} />
                </span>
                <div className="hx-emx-card-label">Generated</div>
                <div className="hx-emx-card-value">
                  {formatNumber(generated)}
                  <small> kWh</small>
                </div>
              </div>
            </div>

            <div className="hx-emx-black">
              <span className="hx-emx-black-title">Net Usage</span>
              <span className="hx-emx-net">
                {formatNumber(netUsage)}
                <small> kWh</small>
              </span>
            </div>

            <div className="hx-emx-billing">
              <div className="hx-emx-billing-row">
                <span className="hx-emx-billing-icon" aria-hidden="true">
                  <MdiIcon path={mdiCurrencyInr} size={25} />
                </span>
                {editingCost ? (
                  <>
                    <input
                      className="hx-emx-cost-input"
                      value={unitCost}
                      onChange={handleCostChange}
                      onKeyDown={(event) => event.key === "Enter" && applyCost()}
                      inputMode="decimal"
                      placeholder="Enter unit cost"
                      aria-label="Unit cost"
                      aria-invalid={Boolean(costError)}
                    />
                    <button type="button" className={`hx-emx-apply ${unitCost.trim() ? "" : "is-disabled"}`} onClick={applyCost}>
                      Apply
                    </button>
                  </>
                ) : (
                  <>
                    <span className="hx-emx-billing-value" style={{ flex: 1 }}>
                      ₹{Number(unitCost).toFixed(2)}
                    </span>
                    <button
                      type="button"
                      className="hx-emx-edit"
                      onClick={() => {
                        setEditingCost(true);
                        setCostError("");
                      }}
                    >
                      <MdiIcon path={mdiPencilOutline} size={20} />
                      Edit
                    </button>
                  </>
                )}
              </div>
              {editingCost && costError ? (
                <p className="hx-emx-error" role="alert">
                  {costError}
                </p>
              ) : null}
              <div className="hx-emx-billing-row">
                <span className="hx-emx-billing-icon" aria-hidden="true">
                  <MdiIcon path={mdiCalendarMonthOutline} size={25} />
                </span>
                <span className="hx-emx-billing-label">Net Units Billed</span>
                <span className="hx-emx-billing-value">
                  {formatNumber(netUsage)}
                  <small> kWh</small>
                </span>
              </div>
            </div>

            <div className="hx-emx-black is-total">
              <span className="hx-emx-black-title">Total cost</span>
              <span className="hx-emx-total">{formatCurrency(totalPrice)}</span>
            </div>
          </>
        )}
      </div>
    </Main>
  );
}

export default EnergyMeterExpense;
